import { nanoid } from 'nanoid';
import type { ImageFile } from '../types/chat.types';

/**
 * Convert file to base64 data URL
 */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result as string);
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Create object URL for preview
 */
export function createPreviewUrl(file: File): string {
  return URL.createObjectURL(file);
}

/**
 * Revoke object URL
 */
export function revokePreviewUrl(url: string): void {
  URL.revokeObjectURL(url);
}

/**
 * Validate image file
 */
export function isValidImageFile(file: File): boolean {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const maxSize = 10 * 1024 * 1024; // 10MB

  return validTypes.includes(file.type) && file.size <= maxSize;
}

/**
 * Process image files for upload
 */
export async function processImageFiles(files: File[]): Promise<ImageFile[]> {
  const validFiles = files.filter(isValidImageFile);

  const imageFiles = await Promise.all(
    validFiles.map(async (file) => {
      const dataUrl = await fileToDataUrl(file);
      const preview = createPreviewUrl(file);

      return {
        id: nanoid(),
        file,
        preview,
        dataUrl,
      };
    })
  );

  return imageFiles;
}

/**
 * Cleanup image previews
 */
export function cleanupImagePreviews(images: ImageFile[]): void {
  images.forEach((img) => {
    revokePreviewUrl(img.preview);
  });
}

/**
 * Get image error message
 */
export function getImageErrorMessage(file: File): string {
  if (!isValidImageFile(file)) {
    if (file.size > 10 * 1024 * 1024) {
      return `${file.name}: File too large (max 10MB)`;
    }
    return `${file.name}: Invalid file type`;
  }
  return '';
}
